import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { type ActiveRoleUserRow } from '../../services/userAdministration';

import {
  createIntranetUser,
  getDesignationList,
  getIntranetUser,
  listActiveUserRoles,
  listDivisions,
  updateIntranetUser,
  type DesignationListItem,
  type DivisionListItem,
  type IntranetDivision,
  type IntranetUser,
  type IntranetUserRole,
  type IntranetUserRoleEntry,
} from '../../services/userAdministration';

import { uploadFileToDmsFolder } from '../../services/fileManagement';
import { setFlashToast } from '../../utils/flashToast';
import { alSelClass } from '../../utils/alSelClass';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../utils/requiredValidation';
import { useAuth } from '../../contexts/AuthContext';
import { digitsOnlyMax10, isValidNicOrPassport } from '../../utils/userFieldValidation';
import { validateProfileImageFile } from '../../utils/profileImageUpload';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';
import { useDmsImageObjectUrl } from '../../hooks/useDmsImageObjectUrl';

function nowLabel() {
  return new Date().toLocaleString();
}

function makeEmpty(): IntranetUser {
  return {
    id: `iu-${Math.floor(Math.random() * 9000 + 1000)}`,
    title: '',
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    nicOrPassport: '',
    role: '',
    division: '',
    divisionId: undefined,
    designation: '',
    serviceId: '',
    contactNumber: '',
    userRoles: [],
    status: 'Active',
    profilePicFileRef: '',
    profilePicFileChanged: false,
    createdAt: nowLabel(),
    createdBy: 'System Admin',
    updatedAt: nowLabel(),
    updatedBy: 'System Admin',
  };
}

function normalizeDivisionName(name: string): string {
  return name
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function normalizeRoleName(name: string): string {
  return name
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function isValidEmail(s: string): boolean {
  const t = s.trim();
  return t.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

/** Unicode letters and spaces only — digits and symbols are not shown as the user types. */
function nameLettersAndSpacesOnly(raw: string, maxLen: number): string {
  return raw.replace(/[^\p{L}\s]/gu, '').slice(0, maxLen);
}

export default function IntranetUserFormPage() {
  const { t } = useTranslation();
  const { user: loggedInUser } = useAuth();
  const { userId } = useParams();
  const isEdit = !!userId;
  const nav = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<IntranetUser>(() => makeEmpty());
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState<string>('');
  const remoteProfilePicUrl = useDmsImageObjectUrl(user.profilePictureIconUrl, user.profilePictureIconFullpath);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const savingRef = useRef(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});
  const [pendingRole, setPendingRole] = useState<IntranetUserRole | ''>('');
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [roleItems, setRoleItems] = useState<ActiveRoleUserRow[]>([]);

  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [activeRoleNamesSet, setActiveRoleNamesSet] = useState<Set<string>>(() => new Set());

  const [divisionItems, setDivisionItems] = useState<DivisionListItem[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<string[]>([]);
  const [designationItems, setDesignationItems] = useState<DesignationListItem[]>([]);

  useEffect(() => {
    if (!isEdit || !userId) return;
    let alive = true;
    void getIntranetUser(userId).then((r) => {
      if (!alive) return;
      if (r.success) setUser(r.data);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isEdit, userId]);

  useEffect(() => {
    if (roleOptions.length === 0) {
      setPendingRole('');
    }
  }, [roleOptions.length]);


  useEffect(() => {
    if (!loggedInUser) return;
    let alive = true;
    void listActiveUserRoles().then((res) => {

      if (!alive) return;
      if (res.success) {
        setRoleItems(res.data);
        const names = res.data.map((r) => r.roleName.trim()).filter(Boolean);
        setRoleOptions(names);
        setActiveRoleNamesSet(new Set(names.map((n) => normalizeRoleName(n))));

      } else {
        setRoleItems([]);
        setRoleOptions([]);
      }
    });
    return () => {
      alive = false;
    };
  }, [loggedInUser?.id]);

  useEffect(() => {
    if (!loggedInUser) return;
    let alive = true;
    void listDivisions().then((res) => {
      if (!alive) return;
      if (res.success) {
        setDivisionItems(res.data);
        setDivisionOptions(res.data.map((d) => d.name.trim()).filter(Boolean));
      } else {
        setDivisionItems([]);
        setDivisionOptions([]);
      }
    });
    return () => {
      alive = false;
    };
  }, [loggedInUser?.id]);

  useEffect(() => {
    if (!loggedInUser) return;
    let alive = true;
    void getDesignationList().then((res) => {
      if (!alive) return;
      setDesignationItems(res.success && Array.isArray(res.data) ? res.data : []);
    });
    return () => {
      alive = false;
    };
  }, [loggedInUser?.id]);

  const divisionNameToId = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of divisionItems) m.set(normalizeDivisionName(d.name), d.id);
    return m;
  }, [divisionItems]);

  const roleNameToId = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of roleItems) m.set(normalizeRoleName(r.roleName), r.roleId);
    return m;
  }, [roleItems]);

  useEffect(() => {
    // When editing a user, back-end may provide only division name; infer id once list loads.
    if (!user.division || user.divisionId != null) return;
    const inferred = divisionNameToId.get(normalizeDivisionName(user.division));
    if (inferred == null) return;
    setUser((u) => {
      if (u.divisionId != null) return u;
      return { ...u, divisionId: inferred };
    });
  }, [divisionNameToId, user.division, user.divisionId]);


  useEffect(() => {
    return () => {
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
    };
  }, [profilePreviewUrl]);

  const autoDisplayName = useMemo(() => {
    return `${user.title} ${user.firstName} ${user.lastName}`.replace(/\s+/g, ' ').trim();
  }, [user.title, user.firstName, user.lastName]);

  const hasEditableNameParts = Boolean(user.firstName.trim() || user.lastName.trim());

  const resolvedDisplayName = useMemo(() => {
    if (hasEditableNameParts) return autoDisplayName;
    return user.displayName.trim() || autoDisplayName;
  }, [autoDisplayName, hasEditableNameParts, user.displayName]);

  const designationOptions = useMemo(
    () => designationItems.map((d) => d.name),
    [designationItems]
  );

  const requiredSpecs: RequiredFieldSpec[] = useMemo(
    () => [
      { key: 'title', label: t('admin.intranetUserForm.title'), value: user.title, elementId: 'intranet-title' },
      { key: 'firstName', label: t('admin.intranetUserForm.firstName'), value: user.firstName.trim(), elementId: 'intranet-firstName' },
      { key: 'lastName', label: t('admin.intranetUserForm.lastName'), value: user.lastName.trim(), elementId: 'intranet-lastName' },
      { key: 'email', label: t('profile.emailUsername'), value: user.email.trim(), elementId: 'intranet-email' },
      { key: 'nicOrPassport', label: t('profile.nicPassport'), value: user.nicOrPassport.trim(), elementId: 'intranet-nic' },
      { key: 'userRoles', label: t('profile.userRoles'), value: user.userRoles.length ? '1' : '', elementId: 'intranet-user-roles' },
      { key: 'division', label: t('profile.divisionUnit'), value: user.division, elementId: 'intranet-division' },
      { key: 'serviceId', label: t('profile.serviceId'), value: true, elementId: 'intranet-serviceId' },
    ],
    [t, user.title, user.firstName, user.lastName, user.email, user.nicOrPassport, user.userRoles.length, user.division]
  );

  function clearReqError(key: string) {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  }

  async function handleSave() {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    let keepLocked = false;
    try {
      const errs = validateRequired(requiredSpecs);
      const merged: RequiredErrors = { ...errs };

      if (user.userRoles.length === 0) merged.userRoles = t('admin.intranetUserForm.atLeastOneRole');
      const maxExceeded = t('profile.maxCharLimit');
      if (user.firstName.trim().length > 255) merged.firstName = maxExceeded;
      if (user.lastName.trim().length > 255) merged.lastName = maxExceeded;
      const emailTrimmed = user.email.trim();
      if (emailTrimmed.length > 255) merged.email = maxExceeded;
      else if (!isValidEmail(user.email)) merged.email = t('admin.shared.invalidEmail');
      if ((user.serviceId?.trim().length ?? 0) > 255) {
        merged.serviceId = maxExceeded;
      }
      if (!isValidNicOrPassport(user.nicOrPassport)) {
        merged.nicOrPassport = t('profile.invalidNicPassport');
      }

      const resolvedUserRoles = user.userRoles.map((r) => {
        const id = r.roleId > 0 ? r.roleId : roleNameToId.get(normalizeRoleName(r.roleName));
        return { ...r, roleId: id ?? 0 };
      });
      // CU-MRC-15 safety: do not allow assigning roles that are not in active role list.
      const invalidRole = resolvedUserRoles.some((r) => {

        const nameNorm = normalizeRoleName(r.roleName);
        // If the UI received an unknown role name, treat as invalid.
        return !activeRoleNamesSet.has(nameNorm);
      });
      if (invalidRole) {
        merged.userRoles = t('admin.intranetUserForm.rolesResolveFailed');
      }


      if (resolvedUserRoles.some((r) => r.roleId === 0)) {
        merged.userRoles = t('admin.intranetUserForm.rolesResolveFailed');
      }
      if (resolvedUserRoles.length && !resolvedUserRoles.some((r) => r.defaultRole)) {
        merged.userRoles = t('admin.intranetUserForm.selectDefaultRole');
      }

      if (Object.keys(merged).length) {
        setReqErrors(merged);
        focusFirstInvalid(requiredSpecs, merged);
        return;
      }

      const resolvedDivisionId =
        user.divisionId ??
        divisionNameToId.get(normalizeDivisionName(user.division)) ??
        (divisionItems.length === 1 ? divisionItems[0]?.id : undefined);
      if (resolvedDivisionId == null) {
        const divisionErr = t('admin.intranetUserForm.selectValidDivision');
        setReqErrors((cur) => ({ ...cur, division: divisionErr }));
        focusFirstInvalid(requiredSpecs, { division: divisionErr });
        nav(location.pathname, {
          replace: true,
          state: { toast: { message: t('admin.intranetUserForm.divisionInvalid'), variant: 'error' } },
        });
        return;
      }

      const defRole = resolvedUserRoles.find((x) => x.defaultRole) ?? resolvedUserRoles[0];
      const next: IntranetUser = {
        ...user,
        userRoles: resolvedUserRoles,
        firstName: user.firstName.trim(),
        lastName: user.lastName.trim(),
        roleId: defRole.roleId,
        role: defRole.roleName as IntranetUser['role'],
        divisionId: resolvedDivisionId,
        displayName: resolvedDisplayName,
        email: user.email.trim(),
        nicOrPassport: user.nicOrPassport.trim(),
        contactNumber: user.contactNumber?.trim() ? digitsOnlyMax10(user.contactNumber) : '',
      };

      const r = isEdit && userId ? await updateIntranetUser(userId, next) : await createIntranetUser(next);
      if (!r.success) {
        nav(location.pathname, { replace: true, state: { toast: { message: r.message ?? t('admin.shared.requestFailed'), variant: 'error' } } });
        return;
      }

      if (isEdit && userId) {
        const refreshed = await getIntranetUser(userId);
        if (refreshed.success) {
          setUser({ ...refreshed.data, profilePicFileRef: '', profilePicFileChanged: false });
          if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
          setProfilePreviewUrl('');
        }
      }

      setFlashToast({
        message: isEdit ? t('admin.intranetUserForm.updateSuccess') : t('admin.intranetUserForm.createSuccess'),
        variant: 'success',
      });
      nav('/admin/intranet-users');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <>
      <div className="bc">
        <span>{t('admin.userAdministration')}</span>
        <span className="bc-sep">/</span>
        <Link to="/admin/intranet-users">{t('admin.intranetUsers.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('admin.intranetUserForm.updateTitle') : t('admin.intranetUserForm.addTitle')}
      </div>

      <div className="pg-title">{isEdit ? t('admin.intranetUserForm.updateTitle') : t('admin.intranetUserForm.addTitle')}</div>

      <div className="cr-table-wrap" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--bl)', fontWeight: 800 }}>{t('admin.intranetUserForm.sectionTitle')}</div>

        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18 }}>
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: 999,
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
              aria-hidden="true"
            >
              {profilePreviewUrl ? (
                <img src={profilePreviewUrl} alt={t('profile.profileImageAlt')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : remoteProfilePicUrl ? (
                <img src={remoteProfilePicUrl} alt={t('profile.profileImageAlt')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.currentTarget.files?.[0] ?? null;
                if (!f) return;
                const validation = validateProfileImageFile(f);
                if (!validation.ok) {
                  setFlashToast({ message: validation.message, variant: 'error' });
                  e.currentTarget.value = '';
                  return;
                }

                if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
                const nextPreview = URL.createObjectURL(f);
                setProfilePreviewUrl(nextPreview);

                setUploading(true);
                void (async () => {
                  try {
                    const res = await uploadFileToDmsFolder(f);
                    if (!res.success) {
                      setFlashToast({ message: res.message ?? t('admin.shared.imageUploadFailed'), variant: 'error' });
                      URL.revokeObjectURL(nextPreview);
                      setProfilePreviewUrl('');
                      return;
                    }
                    setUser((u) => ({ ...u, profilePicFileRef: res.data.file_name, profilePicFileChanged: true }));
                  } catch {
                    setFlashToast({ message: t('admin.shared.imageUploadFailed'), variant: 'error' });
                    URL.revokeObjectURL(nextPreview);
                    setProfilePreviewUrl('');
                  }
                })()
                  .finally(() => {
                    setUploading(false);
                  });
              }}
            />

            <button
              className="mfb"
              type="button"
              aria-label={t('profile.uploadImage')}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              title={uploading ? t('profile.uploading') : t('profile.uploadImage')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
            <button
              className="mfb"
              type="button"
              aria-label={t('admin.shared.removeImage')}
              style={{ background: 'color-mix(in srgb, var(--red) 10%, var(--surface))' }}
              onClick={() => {
                if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
                setProfilePreviewUrl('');
                if (fileInputRef.current) fileInputRef.current.value = '';
                setUser((u) => ({
                  ...u,
                  profilePicFileRef: '',
                  profilePicFileChanged: true,
                  profilePictureIconFullpath: undefined,
                  profilePictureIconUrl: undefined,
                }));
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>

            {isEdit ? (
              <span className={`pill ${user.status === 'Active' ? 'p-com' : 'p-arc'}`} style={{ marginLeft: 8 }}>
                {user.status === 'Active' ? t('common.active') : t('common.inactive')}
              </span>
            ) : null}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FieldSelect
              id="intranet-title"
              fieldKey="title"
              label={t('admin.intranetUserForm.title')}
              required
              value={user.title}
              placeholder={t('admin.intranetUserForm.selectTitle')}
              options={['Mr.', 'Ms.', 'Mrs.']}
              error={reqErrors.title}
              onChange={(v) => {
                setUser((u) => ({ ...u, title: v as IntranetUser['title'] }));
                if (v.trim()) clearReqError('title');
              }}
            />
            <FieldInput
              id="intranet-firstName"
              fieldKey="firstName"
              label={t('admin.intranetUserForm.firstName')}
              required
              value={user.firstName}
              placeholder={t('admin.intranetUserForm.firstNamePlaceholder')}
              maxLength={255}
              error={reqErrors.firstName}
              onChange={(v) => {
                const cleaned = nameLettersAndSpacesOnly(v, 255);
                setUser((u) => ({ ...u, firstName: cleaned }));
                if (cleaned.trim()) clearReqError('firstName');
              }}
            />
            <FieldInput
              id="intranet-lastName"
              fieldKey="lastName"
              label={t('admin.intranetUserForm.lastName')}
              required
              value={user.lastName}
              placeholder={t('admin.intranetUserForm.lastNamePlaceholder')}
              maxLength={255}
              error={reqErrors.lastName}
              onChange={(v) => {
                const cleaned = nameLettersAndSpacesOnly(v, 255);
                setUser((u) => ({ ...u, lastName: cleaned }));
                if (cleaned.trim()) clearReqError('lastName');
              }}
            />
            <FieldInput
              label={t('admin.shared.displayName')}
              value={resolvedDisplayName}
              placeholder={resolvedDisplayName || t('admin.intranetUserForm.displayNamePlaceholder')}
              onChange={() => {
                // Intentionally non-editable to keep consistent with Profile page.
              }}
              disabled
            />
            <FieldInput
              id="intranet-email"
              fieldKey="email"
              label={t('profile.emailUsername')}
              required
              value={user.email}
              placeholder={t('admin.intranetUserForm.emailPlaceholder')}
              maxLength={255}
              error={reqErrors.email}
              disabled={isEdit}
              onChange={(v) => {
                const capped = v.slice(0, 255);
                const cleaned = capped.trim();
                setUser((u) => ({ ...u, email: capped }));
                if (cleaned) clearReqError('email');
              }}
              hint={t('profile.emailHint')}
            />
            <FieldInput
              id="intranet-nic"
              fieldKey="nicOrPassport"
              label={t('profile.nicPassport')}
              required
              value={user.nicOrPassport}
              placeholder={t('admin.intranetUserForm.nicPlaceholder')}
              maxLength={15}
              error={reqErrors.nicOrPassport}
              onChange={(v) => {
                const cleaned = v.trim().slice(0, 15);
                setUser((u) => ({ ...u, nicOrPassport: cleaned }));
                if (cleaned) clearReqError('nicOrPassport');
              }}
            />
            <div className="al-f intranet-role-al-f" id="intranet-user-roles">
              <div className="al-l">
                {t('profile.userRoles')} <span className="req">*</span>
              </div>
              <div className="intranet-role-picker-row">
                <select
                  id="intranet-pending-role"
                  className={alSelClass(pendingRole)}
                  value={pendingRole}
                  onChange={(e) => setPendingRole(e.target.value as IntranetUserRole | '')}
                  aria-label={t('admin.intranetUserForm.selectUserRoleToAdd')}
                  aria-invalid={reqErrors.userRoles ? true : undefined}
                  aria-describedby="intranet-user-roles-msg"
                  disabled={roleOptions.length === 0}
                  style={reqErrors.userRoles ? { borderColor: 'var(--red)' } : undefined}
                >
                  {roleOptions.length === 0 ? (
                    <option value="" disabled>
                      {t('common.noDataFound')}
                    </option>
                  ) : (
                    <>
                      <option value="" disabled hidden>
                        {t('admin.intranetUserForm.selectUserRole')}
                      </option>
                      {roleOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <button
                  className="intranet-role-add-btn"
                  type="button"
                  aria-label={t('admin.intranetUserForm.addUserRole')}
                  title={
                    roleOptions.length === 0
                      ? t('admin.intranetUserForm.noRolesAvailable')
                      : pendingRole
                        ? t('admin.intranetUserForm.addRole')
                        : t('admin.intranetUserForm.selectRoleFirst')
                  }
                  disabled={!pendingRole || roleOptions.length === 0}
                  onClick={() => {
                    if (roleOptions.length === 0) {
                      setReqErrors((cur) => ({ ...cur, userRoles: t('admin.intranetUserForm.noRolesData') }));
                      return;
                    }
                    if (!pendingRole) {
                      setReqErrors((cur) => ({ ...cur, userRoles: t('admin.intranetUserForm.selectRoleThenAdd') }));
                      return;
                    }
                    const roleId = roleNameToId.get(normalizeRoleName(pendingRole));
                    if (roleId == null) {
                      setReqErrors((cur) => ({
                        ...cur,
                        userRoles: t('admin.intranetUserForm.roleResolveFailed'),
                      }));
                      return;
                    }
                    setUser((u) => {
                      if (u.userRoles.some((x) => x.roleId === roleId)) return u;
                      const nextRoles: IntranetUserRoleEntry[] = [
                        ...u.userRoles,
                        { roleId, roleName: pendingRole, defaultRole: u.userRoles.length === 0 },
                      ];
                      const def = nextRoles.find((x) => x.defaultRole) ?? nextRoles[0];
                      return {
                        ...u,
                        userRoles: nextRoles,
                        role: def.roleName as IntranetUser['role'],
                        roleId: def.roleId,
                      };
                    });
                    setPendingRole('');
                    clearReqError('userRoles');
                  }}
                >
                  +
                </button>
              </div>
              <div className="intranet-role-rows">
                {user.userRoles.map((r, idx) => (
                  <div key={`${r.roleId}-${idx}`} className="intranet-role-row">
                    <div className="intranet-role-chip">
                      <span className="intranet-role-chip-name">{r.roleName}</span>
                      <button
                        className="intranet-role-chip-remove"
                        type="button"
                        aria-label={t('admin.intranetUserForm.removeRole', { roleName: r.roleName })}
                        onClick={() => {
                          let becameEmpty = false;
                          setUser((u) => {
                            const nextRoles = u.userRoles.filter((_, i) => i !== idx);
                            becameEmpty = nextRoles.length === 0;
                            const withDefault =
                              nextRoles.length === 0
                                ? []
                                : nextRoles.some((x) => x.defaultRole)
                                  ? nextRoles
                                  : nextRoles.map((x, i) => ({ ...x, defaultRole: i === 0 }));
                            const def = withDefault.find((x) => x.defaultRole) ?? withDefault[0];
                            return {
                              ...u,
                              userRoles: withDefault,
                              role: (def?.roleName ?? '') as IntranetUser['role'],
                              roleId: def?.roleId,
                            };
                          });
                          if (becameEmpty) clearReqError('userRoles');
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <label className="intranet-role-def">
                      <input
                        className="intranet-role-def-input"
                        type="radio"
                        name="intranet-default-role"
                        checked={r.defaultRole}
                        onChange={() => {
                          setUser((u) => ({
                            ...u,
                            userRoles: u.userRoles.map((x, i) => ({
                              ...x,
                              defaultRole: i === idx,
                            })),
                            role: r.roleName as IntranetUser['role'],
                            roleId: r.roleId,
                          }));
                        }}
                      />
                      <span className="intranet-role-def-ui" aria-hidden="true" />
                      <span>{t('admin.intranetUserForm.defaultRole')}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div
                id="intranet-user-roles-msg"
                className={reqErrors.userRoles ? 'intranet-role-field-err' : 'intranet-role-hint'}
                role={reqErrors.userRoles ? 'alert' : undefined}
              >
                {reqErrors.userRoles ?? t('admin.intranetUserForm.atLeastOneRole')}
              </div>
            </div>
            <FieldSelect
              id="intranet-division"
              fieldKey="division"
              label={t('profile.divisionUnit')}
              required
              value={user.division}
              placeholder={t('admin.intranetUsers.selectDivisionUnit')}
              options={divisionOptions}
              error={reqErrors.division}
              onChange={(v) => {
                const nm = v as IntranetDivision;
                const id = divisionNameToId.get(normalizeDivisionName(nm));
                setUser((u) => ({ ...u, division: nm, divisionId: id }));
                clearReqError('division');
              }}
            />
            <FieldSelect
              label={t('profile.designation')}
              value={user.designation ?? ''}
              placeholder={t('admin.intranetUserForm.selectDesignation')}
              options={designationOptions}
              onChange={(v) => {
                const matched = designationItems.find((d) => d.name === v);
                setUser((u) => ({ ...u, designation: v, designationId: matched?.id ?? undefined }));
              }}
            />
            <FieldInput
              id="intranet-serviceId"
              fieldKey="serviceId"
              label={t('profile.serviceId')}
              value={user.serviceId ?? ''}
              placeholder={t('admin.intranetUserForm.serviceIdPlaceholder')}
              maxLength={255}
              error={reqErrors.serviceId}
              onChange={(v) => {
                setUser((u) => ({ ...u, serviceId: v.slice(0, 255) }));
                clearReqError('serviceId');
              }}
            />
            <FieldInput
              id="intranet-contactNumber"
              fieldKey="contactNumber"
              label={t('profile.contactNumber')}
              value={user.contactNumber ?? ''}
              placeholder={t('profile.contactPlaceholder')}
              maxLength={10}
              onChange={(v) => setUser((u) => ({ ...u, contactNumber: digitsOnlyMax10(v) }))}
            />

            {isEdit ? (
              <div className="al-f" style={{ justifyContent: 'flex-end' }}>
                <label className="ccr-chk" style={{ marginBottom: 0 }}>
                  <input type="checkbox" checked={user.status === 'Active'} onChange={(e) => setUser((u) => ({ ...u, status: e.target.checked ? 'Active' : 'Inactive' }))} />
                  <span>{t('common.active')}</span>
                </label>
              </div>
            ) : null}
          </div>

          {isEdit ? (
            <SystemInformationSection title={t('profile.systemInformation')}>
              <SystemInfoBox label={t('profile.createdAt')} value={user.createdAt} />
              <SystemInfoBox label={t('profile.createdBy')} value={user.createdBy} />
              <SystemInfoBox label={t('profile.lastUpdatedAt')} value={user.updatedAt} />
              <SystemInfoBox label={t('profile.lastUpdatedBy')} value={user.updatedBy} />
            </SystemInformationSection>
          ) : null}
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('admin.shared.cancelChangesMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/admin/intranet-users');
        }}
      />
    </>
  );
}

function FieldInput({
  id,
  fieldKey,
  label,
  required,
  value,
  placeholder,
  onChange,
  hint,
  error,
  disabled,
  maxLength,
}: {
  id?: string;
  fieldKey?: string;
  label: string;
  required?: boolean;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  hint?: string;
  error?: string;
  disabled?: boolean;
  maxLength?: number;
}) {
  const baseId = id ?? fieldKey ?? label;
  const errId = error ? `${baseId}-err` : undefined;
  const hintId = hint ? `${baseId}-hint` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="al-f">
      <div className="al-l">
        {label} {required ? <span className="req">*</span> : null}
      </div>
      <input
        id={id}
        className="al-inp"
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={error ? { borderColor: 'var(--red)' } : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      {error ? (
        <div id={errId} style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
          {error}
        </div>
      ) : null}
      {hint ? (
        <div id={hintId} style={{ fontSize: 11, color: 'var(--t3)', marginTop: error ? 4 : 6 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function FieldSelect({
  id,
  fieldKey,
  label,
  required,
  value,
  placeholder,
  options,
  onChange,
  error,
}: {
  id?: string;
  fieldKey?: string;
  label: string;
  required?: boolean;
  value: string;
  placeholder?: string;
  options: string[];
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div className="al-f">
      <div className="al-l">
        {label} {required ? <span className="req">*</span> : null}
      </div>
      <select
        id={id}
        className={alSelClass(value)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={options.length === 0}
        style={error ? { borderColor: 'var(--red)' } : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id ?? fieldKey ?? label}-err` : undefined}
      >
        {options.length === 0 ? (
          <option value="" disabled>
            No data found
          </option>
        ) : (
          <>
            {placeholder ? (
              <option value="" disabled hidden>
                {placeholder}
              </option>
            ) : null}
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </>
        )}
      </select>
      {error ? (
        <div id={`${id ?? fieldKey ?? label}-err`} style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
